import { Component, computed, input } from '@angular/core';
import { TLingoMarqueeMood, RidLingoMarquee } from '../rid-lingo-marquee/rid-lingo-marquee';
import { TLingoSpotlightMood } from '../rid-lingo-spotlight/rid-lingo-spotlight';
import { RidLingoTitle } from '../rid-lingo-title/rid-lingo-title';

/**
 * - IDLE: waiting for the player to type
 * - THINKING: leaning in while the tiles are flipped
 * - HAPPY: a guess that found new klanken in the right place, until the keyboard comes back
 * - CHEERING: the word was guessed
 * - SAD: out of guesses
 */
export type TLingoPresenterMood = 'IDLE' | 'THINKING' | 'HAPPY' | 'CHEERING' | 'SAD';

type TLingoPresenterSprite = 'neutral' | 'excited' | 'suspense' | 'celebrating';

const SPRITES = ['neutral', 'excited', 'suspense', 'celebrating'] as const satisfies TLingoPresenterSprite[];

const MOOD_SPRITES: Record<TLingoPresenterMood, TLingoPresenterSprite> = {
  IDLE: 'neutral',
  THINKING: 'suspense',
  HAPPY: 'excited',
  CHEERING: 'celebrating',
  SAD: 'neutral',
};

// Only transitions, so the presenter always moves smoothly from one pose to the next. Turning around the bottom, behind the desk.
const POSES: Record<TLingoPresenterMood, string> = {
  IDLE: '',
  THINKING: '-rotate-2',
  HAPPY: '',
  CHEERING: '',
  SAD: 'translate-y-6 brightness-75 saturate-75',
};

/*
 * Played once whenever the sprite changes, so the swap is hidden in the movement. Every sprite has its own animation, so
 * it restarts on every swap.
 */
const SWAPS: Record<TLingoPresenterSprite, string> = {
  neutral: 'anim-settle',
  excited: 'anim-squash',
  suspense: 'anim-wobble [--wobble-angle:2deg]',
  celebrating: 'anim-jump',
};

const DESK_MOODS: Record<TLingoPresenterMood, TLingoMarqueeMood> = {
  IDLE: 'IDLE',
  THINKING: 'IDLE',
  HAPPY: 'IDLE',
  CHEERING: 'IDLE',
  SAD: 'DIM',
};

const TITLE_MOODS: Record<TLingoPresenterMood, TLingoSpotlightMood> = {
  IDLE: 'IDLE',
  THINKING: 'FOCUS',
  HAPPY: 'IDLE',
  CHEERING: 'IDLE',
  SAD: 'DIM',
};

/**
 * The presenter of the show behind the Lingo desk, reacting to how the player is doing.
 */
@Component({
  selector: 'rid-lingo-presenter',
  imports: [RidLingoMarquee, RidLingoTitle],
  templateUrl: './rid-lingo-presenter.html',
  host: {
    class: 'relative flex flex-col items-center justify-end',
  },
})
export class RidLingoPresenter {
  mood = input<TLingoPresenterMood>('IDLE');
  /** Makes the presenter bigger behind the desk, for when there's more room above it. */
  enlarged = input<boolean>(false);

  readonly SPRITES = SPRITES;

  sprite = computed(() => MOOD_SPRITES[this.mood()]);
  poseClass = computed(() => POSES[this.mood()]);
  swapClass = computed(() => SWAPS[this.sprite()]);
  deskMood = computed(() => DESK_MOODS[this.mood()]);
  titleMood = computed(() => TITLE_MOODS[this.mood()]);
}
